import { Injectable } from "@angular/core";

import { QuoteFeatureState } from "src/app/shared/store/quote/term-life-insurance/quote.state";
import { NotificationMessagePayload } from "../../../../store/notification/notification.actions";
import {
  Code,
  NotificationMessage,
  Severity,
} from "../../../../store/notification/notification.state";
import {
  DEFAULT_NOTIFICATION_TIMEOUT_IN_MILLISECONDS,
  NotificationCoreService,
} from "../../core/notification-core.service";

@Injectable({
  providedIn: "root",
})
export class NotificationQuoteService {
  createNotificationPayloads(
    previousQuoteFeatureState: QuoteFeatureState,
    currentQuoteFeatureState: QuoteFeatureState,
    _: NotificationMessage[] | undefined,
  ) {
    const notificationMessagePayloads: NotificationMessagePayload[] = [];

    const httpNotificationMessage =
      NotificationCoreService.tryCreateHttpNotificationMessage(
        previousQuoteFeatureState,
        currentQuoteFeatureState,
      );

    const insuranceTermInYearsResetNotificationMessage =
      this.tryCreateResetInsuranceTermInYearsNotificationMessage(
        previousQuoteFeatureState,
        currentQuoteFeatureState,
      );

    const insuranceStartDateResetNotificationMessage =
      this.tryCreateResetInsuranceStartDateNotificationMessage(
        previousQuoteFeatureState,
        currentQuoteFeatureState,
      );

    const insuredSumHasBeenIncreasedNotificationMessage =
      this.tryCreateInsuredSumHasChangedNotificationMessage(
        previousQuoteFeatureState,
        currentQuoteFeatureState,
      );

    if (httpNotificationMessage) {
      notificationMessagePayloads.push(httpNotificationMessage);
    }

    if (insuranceTermInYearsResetNotificationMessage) {
      notificationMessagePayloads.push(
        insuranceTermInYearsResetNotificationMessage,
      );
    }

    if (insuranceStartDateResetNotificationMessage) {
      notificationMessagePayloads.push(
        insuranceStartDateResetNotificationMessage,
      );
    }

    if (insuredSumHasBeenIncreasedNotificationMessage) {
      notificationMessagePayloads.push(
        insuredSumHasBeenIncreasedNotificationMessage,
      );
    }

    return notificationMessagePayloads.length > 0
      ? notificationMessagePayloads
      : undefined;
  }

  private tryCreateResetInsuranceTermInYearsNotificationMessage(
    previousQuoteFeatureState: QuoteFeatureState,
    currentQuoteFeatureState: QuoteFeatureState,
  ) {
    const hasInsuranceTermInYearsBeenReset =
      NotificationCoreService.hasValueBeenReset(
        previousQuoteFeatureState.insuranceTermInYears,
        currentQuoteFeatureState.insuranceTermInYears,
        previousQuoteFeatureState.isBusy,
        currentQuoteFeatureState.isBusy,
      );

    if (hasInsuranceTermInYearsBeenReset) {
      const notificationMessagePayload: NotificationMessagePayload = {
        severity: Severity.WARNING,
        code: Code.RESET_INSURANCE_TERM_IN_YEARS,
        deleteAfterNumberOfMilliseconds:
          DEFAULT_NOTIFICATION_TIMEOUT_IN_MILLISECONDS,
        text: $localize`Das Geburtsdatum hat einen Einfluss auf die Auswahlmöglichkeiten der Laufzeit. Aus diesem Grund muss die Laufzeit nochmals gesetzt werden.`,
      };

      return notificationMessagePayload;
    }

    return undefined;
  }

  private tryCreateResetInsuranceStartDateNotificationMessage(
    previousQuoteFeatureState: QuoteFeatureState,
    currentQuoteFeatureState: QuoteFeatureState,
  ) {
    const hasInsuranceStartDateBeenReset =
      NotificationCoreService.hasValueBeenReset(
        previousQuoteFeatureState.insuranceStartDate,
        currentQuoteFeatureState.insuranceStartDate,
        previousQuoteFeatureState.isBusy,
        currentQuoteFeatureState.isBusy,
      );

    if (hasInsuranceStartDateBeenReset) {
      const notificationMessagePayload: NotificationMessagePayload = {
        severity: Severity.WARNING,
        code: Code.OUTDATED_INSURANCE_START_DATE,
        deleteAfterNumberOfMilliseconds:
          DEFAULT_NOTIFICATION_TIMEOUT_IN_MILLISECONDS,
        text: $localize`Der Versicherungsbeginn, der verwendet wurde, ist nicht mehr gültig. Bitte wählen Sie den Versicherungsbeginn erneut aus.`,
      };

      return notificationMessagePayload;
    }

    return undefined;
  }

  private tryCreateInsuredSumHasChangedNotificationMessage(
    previousQuoteFeatureState: QuoteFeatureState,
    currentQuoteFeatureState: QuoteFeatureState,
  ) {
    const isAfterBackendRequestPhase =
      NotificationCoreService.isAfterBackendRequestPhase(
        previousQuoteFeatureState.isBusy,
        currentQuoteFeatureState.isBusy,
      );

    /**
     * We are only interested when the 'insuredSum' changes after we call the backend.
     */
    if (!isAfterBackendRequestPhase) {
      return undefined;
    }

    /**
     * This deals with edge cases such as if we send an 'insuredSum'
     * and receive an error from the backend and thus 'undefined' as the 'insuredSum'.
     */
    const isInsuredSumBeforeBackendRequestDefined =
      previousQuoteFeatureState.insuredSum !== undefined;

    const isInsuredSumAfterBackendRequestDefined =
      currentQuoteFeatureState.insuredSum !== undefined;

    const hasInsuredSumChanged =
      previousQuoteFeatureState.insuredSum !==
      currentQuoteFeatureState.insuredSum;

    if (
      isInsuredSumBeforeBackendRequestDefined &&
      isInsuredSumAfterBackendRequestDefined &&
      hasInsuredSumChanged
    ) {
      const notificationMessagePayload: NotificationMessagePayload = {
        severity: Severity.INFO,
        code: Code.INSURED_SUM_HAS_CHANGED,
        deleteAfterNumberOfMilliseconds:
          DEFAULT_NOTIFICATION_TIMEOUT_IN_MILLISECONDS,
        text: $localize`Die von Ihnen gewünschte Konfiguration ist nicht möglich, da die Mindestprämie unterschritten wird. Wir haben die Versicherungssumme daher entsprechend angepasst.`,
      };

      return notificationMessagePayload;
    }

    return undefined;
  }
}
